/**
 * v3.21-1621 · Global Team Chat context.
 *
 * A single shared source of truth for the chat window's open state, so
 * that opening/closing the chat (via X) never touches Client Workspace,
 * call state, notes, or navigation.
 *
 * Any component can dispatch a global CustomEvent:
 *   window.dispatchEvent(new CustomEvent('team-chat:open',  {detail:{clientId, clientName}}));
 *   window.dispatchEvent(new CustomEvent('team-chat:close'));
 * so no hook import is required (Ghost dock, presence banner, etc.).
 */
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';

const TeamChatCtx = createContext(null);

export function TeamChatProvider({ children }) {
  const [state, setState] = useState({ open: false, clientId: null, clientName: null });

  const openTeamChat = useCallback((clientId, opts = {}) => {
    if (!clientId) return;
    setState({ open: true, clientId, clientName: opts.clientName || null });
  }, []);

  const closeTeamChat = useCallback(() => {
    // v3.21-1621 · X is a UI close.  DO NOT delete server-side history;
    // simply hide the window.  Reopening resumes the same thread.
    setState((prev) => ({ ...prev, open: false }));
  }, []);

  useEffect(() => {
    const onOpen = (e) => {
      const { clientId, clientName } = e.detail || {};
      if (clientId) openTeamChat(clientId, { clientName });
    };
    const onClose = () => closeTeamChat();
    window.addEventListener('team-chat:open',  onOpen);
    window.addEventListener('team-chat:close', onClose);
    return () => {
      window.removeEventListener('team-chat:open',  onOpen);
      window.removeEventListener('team-chat:close', onClose);
    };
  }, [openTeamChat, closeTeamChat]);

  const value = useMemo(() => ({
    ...state, openTeamChat, closeTeamChat,
  }), [state, openTeamChat, closeTeamChat]);

  return <TeamChatCtx.Provider value={value}>{children}</TeamChatCtx.Provider>;
}

export function useTeamChat() {
  const ctx = useContext(TeamChatCtx);
  if (!ctx) {
    return {
      open:       false,
      clientId:   null,
      clientName: null,
      openTeamChat: (clientId, opts = {}) => {
        try {
          window.dispatchEvent(new CustomEvent('team-chat:open',
            { detail: { clientId, clientName: opts.clientName || null } }));
        } catch { /* noop */ }
      },
      closeTeamChat: () => {
        try { window.dispatchEvent(new CustomEvent('team-chat:close')); }
        catch { /* noop */ }
      },
    };
  }
  return ctx;
}
